'use client';
import { JSX, useEffect, useMemo, useState } from 'react';
import { AtText } from '../at-text';
import { ETextVariants } from '@/components/atoms/at-text/at-text.types';
import { EIcons } from '../at-icon/icons';
import { AtIcon } from '../at-icon';
import atSetLocationAction from './at-set-location-action.variants';
import useBreakpoint from '@/hooks/useBreakpoint';
import classNames from 'classnames';
import {
  ESetLocationActionVariants,
  IAtsetLocationActionProps,
} from './at-set-location-action.types';

/**
 * `AtSetLocationAction` is a responsive, styled button for setting or resetting the user's location.
 * It includes a location icon, optional label text, and responsive logic for showing reset state.
 *
 * - If `hasLocation` is true, shows optional reset text.
 * - For the `nav` variant, reset text only appears on small screens.
 * - Button content can be customized via `children`; otherwise it uses fallback text.
 *
 */

export const AtSetLocationAction = ({
  children,
  onClick,
  hasLocation,
  className,
  variant,
}: IAtsetLocationActionProps): JSX.Element => {
  const [isClient, setIsClient] = useState(false);

  const { base, iconColor, iconSize, ctaText, resetText } = atSetLocationAction(
    {
      hasLocation,
      intent: variant,
    },
  );
  const bp = useBreakpoint();

  const defaultText = useMemo(() => {
    if (variant === ESetLocationActionVariants.FILTER) {
      return 'Set Location';
    }

    return 'Set Build Location';
  }, [variant]);

  const showResetText = useMemo(() => {
    if (!hasLocation) {
      return false;
    }

    if (variant === ESetLocationActionVariants.NAV) {
      if (bp === 'xs' || bp === 'sm' || bp == 'md') {
        return true;
      }
      return false;
    }

    return true;
  }, [bp, hasLocation, variant]);

  const childrenText = useMemo(() => {
    if (children) {
      return children;
    }

    return defaultText;
  }, [children, defaultText]);

  useEffect(() => {
    setIsClient(true);
  }, []);
  return (
    <button
      onClick={onClick}
      className={classNames(base(), className)}
      aria-label={
        hasLocation && isClient
          ? `Reset Location button, current location ${children}`
          : 'Set your build location'
      }
    >
      <span className={iconSize()}>
        <AtIcon icon={EIcons.LOCATION} color={iconColor()} />
      </span>
      <span>
        <AtText type="span" className={ctaText()}>
          <span>{isClient && childrenText}</span>
          {showResetText && (
            <AtText
              type="span"
              variant={ETextVariants.LABEL}
              className={classNames(
                'pl-2 text-cta-outline-secondary',
                resetText(),
              )}
            >
              [Reset]
            </AtText>
          )}
        </AtText>
      </span>
    </button>
  );
};
